import { toaster } from '@/components/ui/toaster';
import api from '@/libs/axiosInstance';
import { contactSummaryQueryPrefix } from '@/hooks/queries/use-contact-summary-query';
import type {
  ApiResponse,
  RemoveAffiliateMembershipRequest,
  RemoveAffiliateMembershipResponse,
  RemovedAffiliateMembership,
} from '@fluenceur/types';
import { ErrorCode } from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

export type RemoveAffiliateVariables = RemoveAffiliateMembershipRequest & {
  userId: string;
  programId: string;
};

/** Always reread: a replayed receipt can belong to an episode preceding a rejoin. */
export const useRemoveAffiliateMembership = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const refresh = async ({ userId, programId, brandId }: RemoveAffiliateVariables) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['affiliate-details', userId, programId] }),
      queryClient.invalidateQueries({ queryKey: ['affiliateUser', userId] }),
      queryClient.invalidateQueries({ queryKey: ['contact-affiliation'] }),
      queryClient.invalidateQueries({ queryKey: contactSummaryQueryPrefix(brandId) }),
      queryClient.invalidateQueries({ queryKey: ['affiliates'] }),
      queryClient.invalidateQueries({ queryKey: ['program-invitations'] }),
      queryClient.invalidateQueries({ queryKey: ['invitablePrograms'] }),
    ]);
  };

  return useMutation<RemovedAffiliateMembership, AxiosError<ApiResponse>, RemoveAffiliateVariables>(
    {
      retry: false,
      mutationFn: async ({ userId, programId, ...request }) => {
        const { data } = await api.post<RemoveAffiliateMembershipResponse>(
          `/dashboard/affiliates/${userId}/programs/${programId}/remove`,
          request
        );
        if (!data.success || !data.data) {
          throw new Error(data.message || 'Failed to remove affiliate');
        }
        return data.data;
      },
      onSuccess: async (receipt, variables) => {
        await refresh(variables);
        toaster.success({
          title: t(
            receipt.replayed ? 'affiliates.removal.refreshed' : 'affiliates.removal.success'
          ),
        });
      },
      onError: async (error, variables) => {
        if (error.response?.status === 409 || error.response?.status === 403) {
          await refresh(variables);
        }
        const data = error.response?.data;
        const fallback =
          data?.code === ErrorCode.E_FORBIDDEN
            ? t('templates.permissionNotice.title')
            : data?.code === ErrorCode.E_AFFILIATE_NOT_FOUND
              ? t('affiliates.removal.failed')
              : data?.message || t('affiliates.removal.retry');
        toaster.error({
          title: t('affiliates.removal.failed'),
          description: data?.code ? t(`errors.${data.code}`, { defaultValue: fallback }) : fallback,
        });
      },
    }
  );
};
